import { styled } from "goober";
import { forwardRef, useRef, useState } from "react";
import { Tooltip } from "./Tooltip";

function assignRef<T>(ref: React.Ref<T> | undefined, el: T | null) {
  if (typeof ref === "function") ref(el);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = el;
}

const Element = styled("span")`
  font-display: swap;
  hanging-punctuation: first;
  font-variant-numeric: tabular-nums lining-nums;
  font-variant-ligatures: contextual;
`;

export const variantStyles = {
  "display-md": {
    fontFamily: "inherit",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "24px",
    letterSpacing: "-1%",
  },
  "text-regular": {
    fontFamily: "inherit",
    fontWeight: 350,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "-1%",
  },
  "text-regular-md": {
    fontFamily: "inherit",
    fontWeight: 450,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
  },
  "text-small": {
    fontFamily: "inherit",
    fontWeight: 350,
    fontSize: "10px",
    lineHeight: "12px",
    letterSpacing: "-1%",
  },
  "text-smaller-md": {
    fontFamily: "inherit",
    fontWeight: 450,
    fontSize: "9px",
    lineHeight: "12px",
    letterSpacing: "-1%",
  },
  "text-small-md": {
    fontFamily: "inherit",
    fontWeight: 450,
    fontSize: "10px",
    lineHeight: "12px",
    letterSpacing: 0,
  },
  "code-regular": {
    fontFamily: "var(--font-mono)",
    fontWeight: 350,
    fontSize: "11px",
    lineHeight: "20px",
    letterSpacing: 0,
  },
  "code-small": {
    fontFamily: "var(--font-mono)",
    fontWeight: 350,
    fontSize: "10px",
    lineHeight: "12px",
    letterSpacing: 0,
  },
};

interface Props {
  children: React.ReactNode;
  variant?: keyof typeof variantStyles;
  color?: string;
  ellipsis?: boolean;
  style?: React.CSSProperties;
}

export const Text = forwardRef<HTMLSpanElement, Props>(function Text(
  {
    children,
    variant = "text-regular",
    color = "currentColor",
    ellipsis = false,
    style: styleProp,
  },
  ref
) {
  const variantStyle = variantStyles[variant];
  const style = {
    color,
    fontFamily: variantStyle.fontFamily,
    fontWeight: variantStyle.fontWeight,
    fontSize: variantStyle.fontSize,
    lineHeight: variantStyle.lineHeight,
    letterSpacing: variantStyle.letterSpacing,
    ...(ellipsis && {
      display: "block",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      minWidth: 0,
    }),
    ...styleProp,
  };

  // Only string/number content gets the hover tooltip, since the tooltip shows
  // the full value and we can't meaningfully render arbitrary (or interactive)
  // nodes inside it. Non-string ellipsis children still truncate via CSS.
  if (
    ellipsis &&
    (typeof children === "string" || typeof children === "number")
  ) {
    return (
      <TruncatingText forwardedRef={ref} content={children} style={style}>
        {children}
      </TruncatingText>
    );
  }

  return (
    <Element ref={ref} style={style}>
      {children}
    </Element>
  );
});

// When ellipsis-truncated text actually overflows its container, reveal the full
// value in a tooltip on hover.
function TruncatingText({
  children,
  content,
  style,
  forwardedRef,
}: {
  children: React.ReactNode;
  content: string | number;
  style: React.CSSProperties;
  forwardedRef: React.Ref<HTMLSpanElement>;
}) {
  const innerRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // Measured on hover, not eagerly: the tooltip only matters under the
  // pointer, and an effect-time scrollWidth read (plus a ResizeObserver)
  // per Text forces serial re-layouts on lists that render thousands.
  const measure = () => {
    const el = innerRef.current;
    if (el) setOverflowing(el.scrollWidth > el.clientWidth + 1);
  };

  const setRef = (el: HTMLSpanElement | null) => {
    innerRef.current = el;
    assignRef(forwardedRef, el);
  };

  return (
    <Tooltip fill hidden={!overflowing} content={String(content)}>
      <Element ref={setRef} style={style} onMouseEnter={measure}>
        {children}
      </Element>
    </Tooltip>
  );
}
